import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Link } from '@mui/material'

import { useAppDispatch } from 'app/state'
import { ProcessingJob, JobStatusType } from 'features/jobs/types'
import { deleteProcessingJobRequest } from 'features/jobs/state/jobsProcessingSlice'
import { getNodePath } from 'features/data-explorer/utils'
import { createDialogOpener } from 'features/dialogs/openDialog'

import JobItemProcessSQLQueryDialog from './JobItemProcessSQLQueryDialog'
import JobItem from '../JobItem'

const openJobItemProcessErrorDialog = createDialogOpener(() => import('./JobItemProcessErrorDialog'))
const openJobCancelDialog = createDialogOpener(() => import('../common/JobCancelDialog'))

interface JobItemProps {
  job: ProcessingJob
  inlineMode?: boolean
  padded?: boolean
  onCancel?: () => void
  onClose?: () => void
}

function JobProcessingItem({ job, inlineMode = false, padded, onCancel, onClose }: JobItemProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isCustomSQL = job?.response?.metadata.customQuery
  const processingTypeTranslateKey = job?.response?.metadata.processingType
  const [displaySQLQueryDialog, setDisplaySQLQueryDialog] = useState<boolean>(false)

  const { status, message, tooltip } = useMemo(() => {
    switch (job.status?.status) {
      case JobStatusType.Success:
        return {
          status: JobStatusType.Success,
          message: intl.formatMessage({ id: `jobs.${processingTypeTranslateKey}.status.success` }),
          tooltip: intl.formatMessage({ id: `jobs.${processingTypeTranslateKey}.tooltip.success` })
        }
      case JobStatusType.Failure:
        return {
          status: JobStatusType.Failure,
          message: inlineMode
            ? intl.formatMessage({ id: `jobs.${processingTypeTranslateKey}.status.failureInline` })
            : intl.formatMessage(
                { id: `jobs.${processingTypeTranslateKey}.status.failure` },
                {
                  link: (
                    <Link>
                      <FormattedMessage id="common.button.readMore" />
                    </Link>
                  )
                }
              ),
          tooltip: inlineMode ? '' : intl.formatMessage({ id: 'jobs.tooltip.error' })
        }
      default: // JobStatusType.Pending, JobStatusType.Running
        return {
          status: JobStatusType.Running,
          message: inlineMode
            ? `${intl.formatMessage({ id: `jobs.${processingTypeTranslateKey}.status.runningInline` })} ${
                job.connection
              }`
            : intl.formatMessage({ id: `jobs.${processingTypeTranslateKey}.status.running` }),
          tooltip: intl.formatMessage({ id: 'jobs.tooltip.running' })
        }
    }
  }, [job, intl, processingTypeTranslateKey, inlineMode])

  const handleClick = () => {
    if (status === JobStatusType.Success) {
      if (isCustomSQL) {
        setDisplaySQLQueryDialog(true)
      } else {
        navigate(getNodePath({ id: job.response.metadata.name, connectionId: job.response.metadata.connection }))
        onClose && onClose()
      }
    } else if (status === JobStatusType.Failure && !inlineMode) {
      openJobItemProcessErrorDialog({ job })
    }
  }

  const handleCloseSQLDialog = () => {
    setDisplaySQLQueryDialog(false)
  }

  const handleCancelJob = () => {
    openJobCancelDialog({ job, cancelJob })
  }

  const cancelJob = () => {
    if (job.connection) {
      dispatch(deleteProcessingJobRequest({ job }))
    }
    onCancel?.()
  }

  return (
    <>
      {status && (
        <JobItem
          status={status}
          name={job?.response?.metadata?.name || intl.formatMessage({ id: 'jobs.customQuery' })}
          message={message}
          tooltip={tooltip}
          cursorPointer={!inlineMode || status === JobStatusType.Success || status === JobStatusType.Failure}
          onClick={handleClick}
          onCancel={handleCancelJob}
          padded={padded ?? !inlineMode}
        />
      )}
      <JobItemProcessSQLQueryDialog job={job} open={displaySQLQueryDialog} onClose={handleCloseSQLDialog} />
    </>
  )
}

export default JobProcessingItem
